export type EncryptedAsymmetric<_T> = string & {
  readonly __brand: unique symbol;
};
export type EncryptedSymmetric<_T> = string & {
  readonly __brand: unique symbol;
};

const encryptAlgo = { name: "RSA-OAEP", hash: "SHA-256" };
const signAlgo = { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" };
const publicKeyFormat = "spki";
const privateKeyFormat = "pkcs8";

const aesAlgo = { name: "AES-GCM", length: 256 };
const ivLength = 12; // 96 bits, recommended for GCM

const base64ToUint8Array = (base64: string) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const uint8ArrayToBase64 = (bytes: Uint8Array | ArrayBuffer): string => {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = "";
  const chunk = 8192;
  for (let i = 0; i < arr.length; i += chunk) {
    binary += String.fromCharCode(...arr.subarray(i, i + chunk));
  }
  return btoa(binary);
};

export const decryptAsymmetric = async <T>(
  privateKey: string,
  data: EncryptedAsymmetric<T>,
): Promise<T> =>
  JSON.parse(new TextDecoder().decode(
    await crypto.subtle.decrypt(
      encryptAlgo,
      await importKey(privateKey, "decrypt"),
      base64ToUint8Array(data),
    ),
  ));

const importKey = (
  key: string,
  usage: "encrypt" | "decrypt" | "sign" | "verify",
) =>
  crypto.subtle.importKey(
    usage === "decrypt" || usage === "sign"
      ? privateKeyFormat
      : publicKeyFormat,
    base64ToUint8Array(key),
    usage === "decrypt" || usage === "encrypt" ? encryptAlgo : signAlgo,
    false,
    [usage],
  );

const importSymmetricKey = (key: string, usage: KeyUsage[]) =>
  crypto.subtle.importKey(
    "raw",
    base64ToUint8Array(key),
    aesAlgo,
    false,
    usage,
  );

export const encryptAsymmetric = async <T>(publicKey: string, data: T) =>
  uint8ArrayToBase64(
    await crypto.subtle.encrypt(
      encryptAlgo,
      await importKey(publicKey, "encrypt"),
      new TextEncoder().encode(JSON.stringify(data)),
    ),
  ) as EncryptedAsymmetric<T>;

const encodeKey = async (key: CryptoKey, format: "spki" | "pkcs8") =>
  uint8ArrayToBase64(await crypto.subtle.exportKey(format, key));

const encodeKeys = async ({ publicKey, privateKey }: CryptoKeyPair) => ({
  publicKey: await encodeKey(publicKey, publicKeyFormat),
  privateKey: await encodeKey(privateKey, privateKeyFormat),
});

export const generateKeyPair = async (usage: "sign" | "encrypt") =>
  encodeKeys(
    await crypto.subtle.generateKey(
      {
        ...usage === "encrypt" ? encryptAlgo : signAlgo,
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
      },
      true,
      usage === "encrypt" ? ["encrypt", "decrypt"] : ["sign", "verify"],
    ),
  );

export const sign = async (privateKey: string, data: string) =>
  uint8ArrayToBase64(
    await crypto.subtle.sign(
      signAlgo,
      await importKey(privateKey, "sign"),
      new TextEncoder().encode(data),
    ),
  );

export const verify = async (
  signature: string,
  publicKey: string,
  data: string,
) =>
  crypto.subtle.verify(
    signAlgo,
    await importKey(publicKey, "verify"),
    base64ToUint8Array(signature),
    new TextEncoder().encode(data),
  );

export const encryptSymmetric = async <T>(
  key: string,
  data: T,
): Promise<EncryptedSymmetric<T>> => {
  const iv = crypto.getRandomValues(new Uint8Array(ivLength));
  const cryptoKey = await importSymmetricKey(key, ["encrypt"]);
  const encoded = new TextEncoder().encode(JSON.stringify(data));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      { ...aesAlgo, iv },
      cryptoKey,
      encoded,
    ),
  );
  // Prepend IV to ciphertext
  const combined = new Uint8Array(iv.length + ciphertext.length);
  combined.set(iv, 0);
  combined.set(ciphertext, iv.length);
  return uint8ArrayToBase64(combined) as EncryptedSymmetric<T>;
};

export const decryptSymmetric = async <T>(
  key: string,
  data: EncryptedSymmetric<T>,
): Promise<T> => {
  const raw = base64ToUint8Array(data);
  const iv = raw.subarray(0, ivLength);
  const ciphertext = raw.subarray(ivLength);
  const cryptoKey = await importSymmetricKey(key, ["decrypt"]);
  const decrypted = await crypto.subtle.decrypt(
    { ...aesAlgo, iv },
    cryptoKey,
    ciphertext,
  );
  return JSON.parse(new TextDecoder().decode(decrypted));
};

export const generateSymmetricKey = async (): Promise<string> => {
  const key = await crypto.subtle.generateKey(aesAlgo, true, [
    "encrypt",
    "decrypt",
  ]);
  const raw = await crypto.subtle.exportKey("raw", key);
  return uint8ArrayToBase64(raw);
};

export const encryptBinary = async (
  key: string,
  data: ArrayBuffer,
): Promise<ArrayBuffer> => {
  const iv = crypto.getRandomValues(new Uint8Array(ivLength));
  const cryptoKey = await importSymmetricKey(key, ["encrypt"]);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ ...aesAlgo, iv }, cryptoKey, data),
  );
  const combined = new Uint8Array(iv.length + ciphertext.length);
  combined.set(iv, 0);
  combined.set(ciphertext, iv.length);
  return combined.buffer;
};

export const decryptBinary = async (
  key: string,
  data: ArrayBuffer,
): Promise<ArrayBuffer> => {
  const raw = new Uint8Array(data);
  const iv = raw.subarray(0, ivLength);
  const ciphertext = raw.subarray(ivLength);
  const cryptoKey = await importSymmetricKey(key, ["decrypt"]);
  return crypto.subtle.decrypt({ ...aesAlgo, iv }, cryptoKey, ciphertext);
};

export const base64ToBase64Url = (b64: string) =>
  b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

export const base64UrlToBase64 = (b64url: string) => {
  const padded = b64url.replace(/-/g, "+").replace(/_/g, "/");
  const mod = padded.length % 4;
  return mod ? padded + "=".repeat(4 - mod) : padded;
};
